import { useMutation, UseMutationOptions, UseMutationResult, useQuery, UseQueryResult } from "@tanstack/react-query";
import { useSuspenseQuery, UseSuspenseQueryResult } from "@tanstack/react-query";
import {
  IConfigSelection,
  IError,
  IExecutionConfiguration,
  IExecutionConfigurationInput, IExecutionHistoryList
} from "../../components/RightPanel/Execution/ExecutionDetail/Types";
import { HttpRequest, IRequest } from "../../utilities/HttpRequest";
import { IInsightsPayload } from "../../components/RightPanel/Results/Insights/Types";
import { JsonApiDocument } from "../../common/ApiSchemaType/Types";
import { ICodeListItem } from "../../components/RightPanel/Results/CodeListView/interfaces";
import { memoryCache } from "../services/memory-cache.service";
import { IResultSummary } from "../../components/RightPanel/Results/SearchReport/interfaces";

export type ExecutionResult<T> = { data: T };

enum ExecutionEndpoints {
  CreateExecution = "/v1/executions",
  GetExecution = "/v1/executions/{executionConfigurationId}",
  GetExecutionList = "/v1/executions?artefactId={artefactId}",
  ExecutionResults = "/v1/executions/{executionId}/insights?isIncluded={included}",
  RunExecution = "/v1/executions/{executionConfigurationId}",
  UpdateExecution = "/v1/executions/{executionConfigurationId}",
  GetExecutionRules = "/v1/executions/{executionConfigurationId}/artefacts/{artefactId}/rules",
  GetCodeList = "/v1/executions/{executionId}/artefact/{searchArtefactId}/rules/{ruleName}/resources/{resourceId}",
  GetArtefactSummary = "/executions/{cohortExecutionId}/artefacts/summary"
}

const buildHttpConfig = (relativeURL: string, signal?: AbortSignal): IRequest => {
  const httpConfig: IRequest = {
    relativeUrl: relativeURL,
    endPoint: process.env.APP_FEASIBILITY_SERVICE_API
  };

  const toReturn = signal ? { ...httpConfig, signal } : httpConfig;

  return toReturn;
};

export const useCreateExecutionConfiguration =
    (options: UseMutationOptions<JsonApiDocument, IError, IExecutionConfigurationInput>): UseMutationResult => {
      return useMutation<JsonApiDocument, IError, IExecutionConfigurationInput>({
        mutationFn: async (config: IExecutionConfigurationInput) => {
          const request = buildHttpConfig(ExecutionEndpoints.CreateExecution);
          request.request = config;

          return HttpRequest.post(request);
        },
        ...options,
      });
    };

export const useExecutionConfiguration = (
  executionConfigurationId: string
): UseQueryResult<ExecutionResult<IExecutionConfiguration>> => {
  return useQuery({
    enabled: !!executionConfigurationId,
    queryKey: ["executionConfiguration",executionConfigurationId],
    queryFn: async () => {
      const request = buildHttpConfig(ExecutionEndpoints.GetExecution);
      request.relativeUrl = request.relativeUrl.replace("{executionConfigurationId}", executionConfigurationId);

      return HttpRequest.getWithHeader(request);
    },
  });
};

export const useExecutionHistoryList =
    (artefactId: string, signal: AbortSignal): UseQueryResult<ExecutionResult<IExecutionHistoryList>> => {
      return useQuery({
        queryKey: ["executionList", artefactId],
        queryFn: async () => {
          const request = buildHttpConfig(ExecutionEndpoints.GetExecutionList, signal);
          request.relativeUrl = request.relativeUrl.replace("{artefactId}", artefactId);

          return HttpRequest.getWithHeader(request);
        },
      });
    };

export const useInsights =
  (options: UseMutationOptions<unknown, IError, IInsightsPayload>): UseMutationResult => {
    return useMutation<unknown, IError, IInsightsPayload>({
      mutationFn: async (config: IInsightsPayload) => {
        const { executionId, included, ids, type } = config;
        const request = buildHttpConfig(ExecutionEndpoints.ExecutionResults);
        request.relativeUrl = request.relativeUrl
          .replace("{executionId}", executionId)
          .replace("{included}", included.toString());

        const payload = { ids, type: type };
        request.request = payload;

        return HttpRequest.post(request);
      },
      ...options,
    });
  };

export const useRunExecution =
  (options: UseMutationOptions<JsonApiDocument, IError, string>): UseMutationResult  => {
    return useMutation<JsonApiDocument, IError, string>({
      mutationFn: async (executionConfigurationId: string) => {
        const request = buildHttpConfig(ExecutionEndpoints.RunExecution);
        request.relativeUrl = request.relativeUrl.replace("{executionConfigurationId}", executionConfigurationId);

        return HttpRequest.post(request);
      },
      ...options,
    });
  };

export const useUpdateExecutionConfiguration =
  (options: UseMutationOptions<JsonApiDocument, IError, IExecutionConfigurationInput>): UseMutationResult => {
    return useMutation<JsonApiDocument, IError, IExecutionConfigurationInput>({
      mutationFn: async (config: IExecutionConfigurationInput) => {
        const request = buildHttpConfig(ExecutionEndpoints.UpdateExecution);
        request.relativeUrl = request.relativeUrl.replace("{executionConfigurationId}", config.id as string);
        request.request = config;

        return HttpRequest.put(request);
      },
      ...options,
    });
  };

export const useExecutionCodeList = (
  executionId: string, ruleName: string, ruleId: string, artefactId: string
): UseSuspenseQueryResult<ExecutionResult<ICodeListItem[]>> => {
  const config = memoryCache.get<IConfigSelection>("entitySelection") ||  { ids: [], type: null };

  return useSuspenseQuery({
    queryKey: ["codeList", executionId, ruleId, artefactId, config.ids, config.type],
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const request = buildHttpConfig(ExecutionEndpoints.GetCodeList);
      request.relativeUrl =
        request.relativeUrl
          .replace("{executionId}", executionId)
          .replace("{ruleName}", ruleName)
          .replace("{resourceId}", ruleId)
          .replace("{searchArtefactId}", artefactId)
      ;

      const payload = { ids: config.ids, type: config.type };

      return HttpRequest.post({ ...request, request: payload });
    }
  });
};

export const useGetArtefactSummary = (
  cohortExecutionId: string
): UseSuspenseQueryResult<ExecutionResult<IResultSummary>> => {
  return useSuspenseQuery({
    queryKey: ["artefactSummary", cohortExecutionId],
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const request = buildHttpConfig(ExecutionEndpoints.GetArtefactSummary);
      request.relativeUrl =
        request.relativeUrl
          .replace("{cohortExecutionId}", cohortExecutionId)
      ;
      request.endPoint = process.env.APP_COHORT_BUILDER;

      return HttpRequest.getWithHeader(request);
    }
  });
};
